import React, { FC, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// Shared UI Library Components
import {
  CropImageModal,
  CustomButton,
  CustomIcon,
  DiscardModal,
  EIconName
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Store entities
import { userPhotoDelete, userPhotoUpdate } from '../../../../store/user/user.thunks';

// Styles
import './user-photo.scss';

type TUserPhotoProps = {
  photo?: string;
};

export const UserPhoto: FC<TUserPhotoProps> = (
  { photo = '' }: TUserPhotoProps
) => {

  const dispatch = useDispatch<any>();
  const randomString = Math.random().toString(36);

  const [savingChanges, setSavingChanges] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState<string>(randomString);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSelectedImage(file ? URL.createObjectURL(file) : null);
    setCropModalOpen(true);
  };

  const handleDeletePhoto = () => {
    setDeleteModalOpen(true);
    setInputKey(randomString);
  }

  const handleChangePhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  const updatePhoto = async (file: File): Promise<void> => {
    setSavingChanges(true);
    await dispatch(userPhotoUpdate({ file }));
    setSavingChanges(false);
    setCropModalOpen(false);
    setInputKey(randomString);
  }

  const handleCloseModal = () => {
    setCropModalOpen(false);
    setSelectedImage(null);
    setInputKey(randomString);
  }

  const cancelRemoveDialog = () => {
    setDeleteModalOpen(false);
  }

  const removePhoto = async (): Promise<void> => {
    setDeleteModalOpen(false);
    await dispatch(userPhotoDelete());
  }

  return (
    <div className="user-photo">

      { selectedImage && cropModalOpen && <CropImageModal
        open={ cropModalOpen }
        imageSrc={ selectedImage }
        closeIcon={ false }
        loadingSubmit={ savingChanges }
        onSubmit={ updatePhoto }
        onCancel={ handleCloseModal }/> }

      <div className="user-photo--avatar">
        { photo
          ? <img src={ photo } alt="user-avatar"/>
          : <div className={ 'no-image' }>
            <CustomIcon name={ EIconName.PersonOff } skipSize={true}/>
          </div> }

        { photo
          ? <div className={ 'user-photo--actions' }>
            <CustomIcon name={ EIconName.Delete } onClick={ handleDeletePhoto }/>
            <CustomButton type="text" text={ 'Change' } size={ 'small' } onClick={ handleChangePhoto }/>
          </div>
          : <div className={ 'user-photo--actions' }>
            <CustomButton type="text" text={ 'Add' } size={ 'small' } onClick={ handleChangePhoto }/>
          </div> }
      </div>

      <input
        type="file"
        accept="image/*"
        ref={ fileInputRef }
        key={ inputKey }
        style={ { display: 'none' } }
        onChange={ handleImageChange }
      />

      <DiscardModal open={ deleteModalOpen }
                    onCancel={ (value) => value
                      ? removePhoto()
                      : cancelRemoveDialog() }
                    title={ 'Are you sure you want to delete your Profile Picture?' }/>
    </div>
  );
}
